import { queryOptions } from '@tanstack/react-query';
import type { CourierProfileId } from '../model/schema';
import type { CourierTransactionsSearch } from '../model/transactions';
import type { CourierOrdersSearch } from '../model/orders';
import { courierProfileApi } from './courier-profile-api';
import { courierTransactionsApi } from './courier-transactions-api';

/**
 * Query-key factory + query-options for the Courier-Detail domain. Every hook
 * and (future) mutation invalidates through `courierProfileKeys` — never an
 * inline array (CLAUDE.md). Ids are stringified in the key so `413413` and
 * `'413413'` share a cache entry.
 */
export const courierProfileKeys = {
  all: ['courier-profile'] as const,
  transactions: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'transactions', String(id)] as const,
  details: () => [...courierProfileKeys.all, 'detail'] as const,
  detail: (id: CourierProfileId) =>
    [...courierProfileKeys.details(), String(id)] as const,
  orders: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'orders', String(id)] as const,
  orderList: (id: CourierProfileId, filter: CourierOrdersSearch) =>
    [...courierProfileKeys.orders(id), filter] as const,
  contract: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'contract', String(id)] as const,
  equipment: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'equipment', String(id)] as const,
  location: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'location', String(id)] as const,
  scooterOptions: (search: string) =>
    [...courierProfileKeys.all, 'scooter-options', search] as const,
  batteryOptions: (search: string) =>
    [...courierProfileKeys.all, 'battery-options', search] as const,
  batteryLocation: (batteryId: CourierProfileId, courierId: CourierProfileId) =>
    [
      ...courierProfileKeys.all,
      'battery-location',
      String(batteryId),
      String(courierId),
    ] as const,
  blocks: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'blocks', String(id)] as const,
  ratings: (id: CourierProfileId, page: number) =>
    [...courierProfileKeys.all, 'ratings', String(id), page] as const,
  tests: (id: CourierProfileId) =>
    [...courierProfileKeys.all, 'tests', String(id)] as const,
};

export const courierProfileQueries = {
  transactions: (id: CourierProfileId, filter: CourierTransactionsSearch) =>
    queryOptions({
      queryKey: [...courierProfileKeys.transactions(id), filter],
      queryFn: () => courierTransactionsApi.list(id, filter),
      enabled: Boolean(id),
    }),
  detail: (id: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.detail(id),
      queryFn: () => courierProfileApi.getById(id),
    }),

  orders: (id: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.orders(id),
      queryFn: () => courierProfileApi.getOrders(id),
    }),

  orderList: (id: CourierProfileId, filter: CourierOrdersSearch) =>
    queryOptions({
      queryKey: courierProfileKeys.orderList(id, filter),
      queryFn: () => courierProfileApi.listOrders(id, filter),
    }),

  contract: (id: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.contract(id),
      queryFn: () => courierProfileApi.getContract(id),
    }),

  equipment: (id: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.equipment(id),
      queryFn: () => courierProfileApi.getEquipment(id),
    }),

  location: (id: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.location(id),
      queryFn: () => courierProfileApi.getLocation(id),
      refetchInterval: 5_000,
    }),

  batteryLocation: (batteryId: CourierProfileId, courierId: CourierProfileId) =>
    queryOptions({
      queryKey: courierProfileKeys.batteryLocation(batteryId, courierId),
      queryFn: () => courierProfileApi.getBatteryLocation(batteryId, courierId),
      enabled: Boolean(batteryId),
      refetchInterval: 5_000,
    }),
};
